import {
  tradingCapabilities,
  type AccountRequirement,
  type CapabilityName,
  type PaymentAccount,
} from "./types";

/** Where a business stands on the way to taking money. `no-account` and
 *  `capability-unavailable` are the two the partner cannot walk out of on their
 *  own: the portal cannot create an account, and a capability the account never
 *  had cannot be added by onboarding (lastgo-api ADR 0004). `restricted` is an
 *  account Stripe has since disabled — not a first-time review, and it does not
 *  clear itself. */
export type OnboardingStage =
  | "no-account"
  | "unknown"
  | "tradeable"
  | "capability-unavailable"
  | "action-needed"
  | "restricted"
  | "verifying";

export function unavailableCapabilities(account: PaymentAccount): CapabilityName[] {
  return tradingCapabilities.filter((name) => {
    const { status } = account.capabilities[name];
    return status === null || status === "unsupported";
  });
}

export function restrictedCapabilities(account: PaymentAccount): CapabilityName[] {
  return tradingCapabilities.filter((name) => account.capabilities[name].status === "restricted");
}

/** Stripe answers `user` for the half the partner fills in themselves; the rest
 *  is Stripe's own review and no link will hurry it. */
export function outstandingRequirements(account: PaymentAccount): AccountRequirement[] {
  return account.requirements.filter((requirement) => requirement.awaitingActionFrom === "user");
}

export function onboardingStage(account: PaymentAccount): OnboardingStage {
  if (!account.accountId) return "no-account";
  if (account.canTrade) return "tradeable";
  // Every capability reads null when the account has never been read, which is
  // indistinguishable from a genuinely absent one — so say nothing is known.
  if (account.syncError !== null && account.syncedAt === null) return "unknown";
  if (unavailableCapabilities(account).length > 0) return "capability-unavailable";
  if (outstandingRequirements(account).length > 0) return "action-needed";
  if (restrictedCapabilities(account).length > 0) return "restricted";
  return "verifying";
}

/** An account that already trades is never re-onboarded from here: a payout
 *  target editable through a shared access code is redirected revenue. */
export function canRequestOnboardingLink(account: PaymentAccount): boolean {
  return account.accountId !== null && !account.canTrade;
}
